package com.example.monetix.ui.dialogs

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.animateContentSize
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Search
import androidx.compose.material3.*
import com.example.monetix.ui.components.MonetixTextField
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import io.ktor.client.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import kotlinx.coroutines.launch
import coil3.compose.AsyncImage

@Composable
private fun StableWebTextField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String = "",
    placeholder: String = "",
    modifier: Modifier = Modifier,
    trailingIcon: @Composable (() -> Unit)? = null,
    keyboardType: KeyboardType = KeyboardType.Text,
    interactionSource: MutableInteractionSource = remember { MutableInteractionSource() }
) {
    var localText by remember(value) { mutableStateOf(value) }

    MonetixTextField(
        value = localText,
        onValueChange = { newText ->
            if (localText.length - newText.length > 1 && newText.isBlank()) {
                val rescuedText = "$localText "
                localText = rescuedText
                onValueChange(rescuedText)
            } else {
                localText = newText
                onValueChange(newText)
            }
        },
        label = if (label.isNotBlank()) { { Text(label, style = MaterialTheme.typography.labelSmall) } } else null,
        placeholder = if (placeholder.isNotBlank()) { { Text(placeholder) } } else null,
        maxLines = 1,
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(14.dp),
        trailingIcon = trailingIcon,
        interactionSource = interactionSource,
        keyboardOptions = KeyboardOptions(
            keyboardType = keyboardType,
            autoCorrectEnabled = false
        ),
        colors = OutlinedTextFieldDefaults.colors(
            focusedTextColor = Color.White,
            unfocusedTextColor = Color.White,
            focusedContainerColor = Color.Black.copy(alpha = 0.35f),
            unfocusedContainerColor = Color.Black.copy(alpha = 0.25f),
            focusedBorderColor = Color(0xFF60A5FA),
            unfocusedBorderColor = Color.White.copy(alpha = 0.20f),
            focusedLabelColor = Color.White,
            unfocusedLabelColor = Color(0xFFA0A0A0)
        )
    )
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun WebLogoSearchDialog(
    onDismiss: () -> Unit,
    onLogoSelected: (String) -> Unit
) {
    var query by remember { mutableStateOf("") }
    var isSearching by remember { mutableStateOf(false) }
    var results by remember { mutableStateOf<List<String>>(emptyList()) }
    var errorMsg by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    var directUrl by remember { mutableStateOf("") }

    val bgGradient = remember {
        Brush.verticalGradient(
            colors = listOf(
                Color(0xFF0284C7).copy(alpha = 0.75f),
                Color(0xFF0C4A6E),
                Color(0xFF0F172A)
            )
        )
    }

    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(
            usePlatformDefaultWidth = false,
            dismissOnClickOutside = true,
            dismissOnBackPress = true
        )
    ) {
        Surface(
            modifier = Modifier.fillMaxSize(),
            color = Color.Transparent
        ) {
            Box(modifier = Modifier.fillMaxSize().background(bgGradient)) {
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .systemBarsPadding()
                ) {
                    // 1. HEADER FISSO
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 16.dp, vertical = 16.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        IconButton(
                            onClick = onDismiss,
                            modifier = Modifier.size(40.dp).background(Color.White.copy(alpha = 0.12f), CircleShape)
                        ) {
                            Icon(
                                Icons.Default.ArrowBack,
                                contentDescription = "Back",
                                tint = Color.White
                            )
                        }
                    }

                    Text(
                        "Ricerca Logo Web",
                        style = MaterialTheme.typography.headlineMedium.copy(fontWeight = FontWeight.Bold),
                        color = Color.White,
                        modifier = Modifier.padding(horizontal = 24.dp)
                    )

                    Spacer(modifier = Modifier.height(24.dp))

                    // 2. CORPO SCORREVOLE
                    Column(
                        modifier = Modifier
                            .weight(1f)
                            .verticalScroll(rememberScrollState())
                            .padding(horizontal = 24.dp)
                            .animateContentSize(),
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        // Pannello Ricerca Automatica
                        Surface(
                            shape = RoundedCornerShape(14.dp),
                            color = Color.White.copy(alpha = 0.08f),
                            border = BorderStroke(1.dp, Color.White.copy(alpha = 0.15f)),
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Column(
                                modifier = Modifier.padding(16.dp),
                                verticalArrangement = Arrangement.spacedBy(12.dp)
                            ) {
                                Text("Ricerca Automatica", style = MaterialTheme.typography.labelMedium, color = Color.Gray)

                                StableWebTextField(
                                    value = query,
                                    onValueChange = { query = it },
                                    placeholder = "Es. Netflix, Spotify, Amazon...",
                                    keyboardType = KeyboardType.Email,
                                    trailingIcon = {
                                        IconButton(
                                            onClick = {
                                                if (query.isNotBlank()) {
                                                    isSearching = true
                                                    errorMsg = null
                                                    results = emptyList()

                                                    scope.launch {
                                                        try {
                                                            val client = HttpClient()
                                                            val safeQuery = query.trim().replace(" ", "%20")
                                                            val response = client.get("https://autocomplete.clearbit.com/v1/companies/suggest?query=$safeQuery")
                                                            val responseText = response.bodyAsText()
                                                            client.close()

                                                            val domainRegex = """"domain"\s*:\s*"([^"]+)"""".toRegex()
                                                            val matches = domainRegex.findAll(responseText)

                                                            results = matches.mapNotNull { matchResult ->
                                                                val domain = matchResult.groupValues[1]
                                                                if (domain.isNotBlank()) "https://icon.horse/icon/$domain" else null
                                                            }.toList().distinct()

                                                            if (results.isEmpty()) errorMsg = "Nessun logo trovato."
                                                        } catch (e: Exception) {
                                                            errorMsg = "Errore di connessione."
                                                        } finally {
                                                            isSearching = false
                                                        }
                                                    }
                                                }
                                            }
                                        ) {
                                            Icon(Icons.Default.Search, contentDescription = "Cerca", tint = Color.White)
                                        }
                                    }
                                )

                                if (isSearching) {
                                    LinearProgressIndicator(modifier = Modifier.fillMaxWidth(), color = Color.White)
                                } else if (errorMsg != null) {
                                    Text(errorMsg!!, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodySmall)
                                }

                                if (results.isNotEmpty()) {
                                    Text("Risultati trovati:", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                                    LazyRow(
                                        modifier = Modifier.fillMaxWidth(),
                                        horizontalArrangement = Arrangement.spacedBy(12.dp),
                                        contentPadding = PaddingValues(vertical = 4.dp)
                                    ) {
                                        items(results) { logoUrl ->
                                            Surface(
                                                modifier = Modifier
                                                    .size(64.dp)
                                                    .clickable { onLogoSelected(logoUrl) },
                                                shape = RoundedCornerShape(14.dp),
                                                color = Color.White,
                                                border = BorderStroke(1.dp, Color.LightGray.copy(alpha = 0.2f))
                                            ) {
                                                AsyncImage(
                                                    model = logoUrl,
                                                    contentDescription = null,
                                                    modifier = Modifier.padding(8.dp).fillMaxSize(),
                                                    contentScale = ContentScale.Fit
                                                )
                                            }
                                        }
                                    }
                                }
                            }
                        }

                        // Pannello Link Diretto
                        Surface(
                            shape = RoundedCornerShape(14.dp),
                            color = Color.White.copy(alpha = 0.08f),
                            border = BorderStroke(1.dp, Color.White.copy(alpha = 0.15f)),
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Column(
                                modifier = Modifier.padding(16.dp),
                                verticalArrangement = Arrangement.spacedBy(12.dp)
                            ) {
                                Text("Incolla Link Diretto", style = MaterialTheme.typography.labelMedium, color = Color.Gray)

                                StableWebTextField(
                                    value = directUrl,
                                    onValueChange = { directUrl = it },
                                    label = "https://indirizzo-immagine.png",
                                    keyboardType = KeyboardType.Uri,
                                    trailingIcon = {
                                        IconButton(
                                            onClick = { if (directUrl.isNotBlank()) onLogoSelected(directUrl.trim()) },
                                            enabled = directUrl.isNotBlank()
                                        ) {
                                            Icon(
                                                Icons.Default.CheckCircle,
                                                contentDescription = "Usa",
                                                tint = if (directUrl.isNotBlank()) Color.White else Color.Gray
                                            )
                                        }
                                    }
                                )

                                AnimatedVisibility(visible = directUrl.isNotBlank()) {
                                    Row(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .background(Color(0xFF2C2C2E), RoundedCornerShape(14.dp))
                                            .padding(12.dp),
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        Surface(
                                            modifier = Modifier.size(44.dp),
                                            shape = RoundedCornerShape(14.dp),
                                            color = Color.White
                                        ) {
                                            AsyncImage(
                                                model = directUrl.trim(),
                                                contentDescription = null,
                                                modifier = Modifier.fillMaxSize().padding(4.dp),
                                                contentScale = ContentScale.Fit
                                            )
                                        }
                                        Spacer(Modifier.width(12.dp))
                                        Text(
                                            "Se vedi l'anteprima, clicca la spunta per confermare.",
                                            style = MaterialTheme.typography.bodySmall,
                                            color = Color.LightGray
                                        )
                                    }
                                }
                            }
                        }
                        
                        Spacer(modifier = Modifier.height(80.dp)) // Spazio per non coprire il FAB
                    }
                }

                // 3. FAB at bottom right
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .systemBarsPadding()
                        .padding(24.dp),
                    contentAlignment = Alignment.BottomEnd
                ) {
                    FloatingActionButton(
                        onClick = onDismiss,
                        containerColor = Color(0xFF1C1C1E),
                        contentColor = Color.White,
                        shape = RoundedCornerShape(14.dp)
                    ) {
                        Icon(
                            imageVector = Icons.Default.Close,
                            contentDescription = "Chiudi"
                        )
                    }
                }
            }
        }
    }
}
